export const meta = {
  title: 'Acceptable Use Policy | Hotel Lobby AI',
  description:
    'Content rules for Hotel Lobby AI: child safety, prohibited AI content, deepfakes and impersonation, prompt screening, and how to report violations.',
  updated_at: '2026-10-03',
};

## Purpose

Hotel Lobby AI generates AI images and videos of real people from uploaded photos. This policy sets out what you may and may not create. It is part of our [Terms of Service](/terms-of-service) and applies to every photo, styling note and generated result.

## Child safety — zero tolerance

We have zero tolerance for child sexual abuse material (CSAM) and any content that sexualizes, exploits or endangers minors.

- Never upload, request or generate sexual, suggestive, nude or partially clothed content involving anyone under 18, or anyone who appears to be under 18, including AI-generated, drawn or "fictional" minors.
- Never use the service to groom, harass, shame or endanger a child, or to depict a child in violent, degrading or dangerous situations.
- A photo of a person under 18 may only be uploaded by that child's parent or legal guardian, and only for ordinary, wholesome family content.

Any suspected CSAM is removed immediately, the account is permanently terminated, related data is preserved as the law requires, and we report it to the National Center for Missing & Exploited Children (NCMEC) and/or the relevant law enforcement authority.

## Prohibited content

You may not use Hotel Lobby AI to create, request or share:

- **Sexual or intimate content** of any kind, including nudity, sexual acts, sexualized poses or "undressing" someone in a photo.
- **Non-consensual intimate imagery**, real or synthetic, of any person.
- **Deepfakes of people who have not consented.** Only upload photos of yourself or of people who have given you permission to create this content.
- **Impersonation**, including content that passes off a real person, brand or public figure as saying or doing something they did not, or that is used for fraud, scams or misleading endorsements.
- **Harassment, bullying, hate or threats** targeting a person or group, including content that mocks or demeans someone based on protected characteristics.
- **Violence, self-harm or extremism**, including gore and content that promotes terrorism or violent extremism.
- **Illegal content**, or content that infringes copyright, trademark, privacy or publicity rights.
- **Misinformation intended to deceive**, such as fake news footage or election-related deception.

## Safety measures and prompt screening

- Every optional styling note is checked before generation by our own filter and by an automated prompt-screening service. Requests that are flagged are refused before any image or video is generated.
- Our AI providers apply their own safety filters to images and videos.
- We may review content, refuse any request, and remove generated content that breaks this policy.
- Do not try to bypass these measures — for example with misspellings, coded language or other tricks. Attempting to evade safety systems is itself a violation.

## Reporting violations

If you see content generated with Hotel Lobby AI that breaks this policy, or believe your likeness was used without consent, report it:

- **Email:** [support@hotel-lobby.org](mailto:support@hotel-lobby.org) with the subject line "Abuse report".
- **Signed-in users:** you can also open a ticket in **Settings → Support Tickets**.
- **Include:** a link or screenshot of the content, where you found it, and why it violates this policy. If it shows you, say so — we prioritize reports from the person depicted.

**Response times:**

- **Child safety (CSAM) reports:** acted on within **24 hours** — content removed, account terminated and reported to the authorities.
- **Non-consensual intimate imagery or deepfakes of a reporting person:** content removed within **48 hours** of a valid report.
- **All other reports:** acknowledged within **2 business days** and resolved within **7 days**.

We do not tell the reported user who filed a report. If your own content was removed and you believe it was a mistake, reply to our notice or email support within 30 days to appeal.

## Enforcement

Depending on severity, violations lead to refusal or removal of the content, a warning, suspension or permanent termination of the account, and forfeiture of remaining credits without refund, as described in our [Terms of Service](/terms-of-service). We cooperate with law enforcement and report illegal content where required.

## Changes

We may update this policy as the service and the law evolve. The revision date appears above.
